// CashierScene — terminal scoring scene (Wave 3, Plan 05).
//
// Auto-locks the best 5 of up to 7 cards from RunState.pocket via
// `bestOfPocket` (D-16) — no manual selection UI in Phase 1. Writes the
// result to RunState.cashier so __TEST__.state().cashier reflects it
// (D-19). Phase-1 score is a placeholder; Phase 4 (SCORE-01) replaces
// the formula with the real chip-multiplier-time-bonus stack.
//
// Anti-Pattern 1 (RESEARCH, audience-fatal PITFALL #6): NO inline poker
// logic in scenes. The ONLY hand-evaluation call is `bestOfPocket`. Any
// future refactor that re-derives flush/straight/kicker rules at this
// layer is a regression — the corpus tests in handEval.test.ts gate the
// eval lib, and inline scene logic bypasses that gate.

import Phaser from 'phaser';
import { TestSeam } from '../debug/TestSeam';
import { RunState } from '../run/RunState';
import { bestOfPocket } from '../poker/handEval';
import { makeCardGlyph, CARD_GLYPH_VIEWPORT } from '../ui/CardGlyph';

// Visual placeholder constants — Phase 3 swaps in real card art and
// these all become unused. The card-glyph palette + per-card dimensions
// now live in src/ui/CardGlyph.ts (Plan 02-2 Task 2.1) so CashierScene
// and ShareCardSystem render through the same primitive at different
// scales. Only the inter-card gap remains here as it is layout math
// (not a per-glyph internal).
const CARD_GAP = 4;

export class CashierScene extends Phaser.Scene {
  constructor() {
    super('CashierScene');
  }

  create(): void {
    TestSeam.setScene('CashierScene');

    const result = bestOfPocket(RunState.pocket);
    if (!result) {
      // Should never happen in production: RunScene.tryEnterCashier
      // guards on pocket.length >= 5 (D-17). Fail loud rather than
      // silent so any future regression to the gate is obvious.
      this.add.text(
        this.cameras.main.width / 2,
        this.cameras.main.height / 2,
        'CASHIER: insufficient pocket',
        { fontFamily: 'monospace', fontSize: '12px', color: '#ff5566' },
      ).setOrigin(0.5);
      // Still wire the menu return so the player isn't trapped.
      this.input.keyboard?.on('keydown-M', () => this.scene.start('MenuScene'));
      this.input.keyboard?.on('keydown-ENTER', () => this.scene.start('MenuScene'));
      return;
    }

    // Persist the result to RunState (D-19 plain Card[] not Phaser
    // objects — the cards array from `bestOfPocket` is already a
    // normalized Card[] per evaluateHand's mapping in handEval.ts).
    //
    // handName stores `result.descr`, NOT `result.name`. Per Plan 02
    // SUMMARY: pokersolver returns name='Straight Flush' descr='Royal
    // Flush' for the royal — descr is the human-readable label that the
    // HUD already uses (Plan 04 SUMMARY) and that Plan 05 success
    // criterion #4 requires (`state().cashier.handName === 'Royal
    // Flush'` for the royal). The "field stored = label displayed"
    // invariant means the test seam reflects exactly what the player
    // sees, with no library-quirk skew.
    //
    // Phase 1 placeholder score: pokersolver rank ordinal scaled. Phase
    // 4 (SCORE-01) replaces with chip × multiplier × time-bonus stack.
    const placeholderScore = result.rank * 100;
    RunState.cashier = {
      locked5: result.cards,
      handName: result.descr,
      score: placeholderScore,
    };

    // Layout — render hand label at top, 5 cards centered, score below.
    const cx = this.cameras.main.width / 2;
    const titleY = 24;
    const cardsY = 80;
    const scoreY = 160;

    this.add.text(cx, titleY, 'CASHIER', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#f5e1c8',
    }).setOrigin(0.5);

    // Display `result.descr` (the human label). pokersolver returns
    // name='Straight Flush' descr='Royal Flush' for the royal — descr
    // is the HUD-friendly field already used by PocketStrip (Plan 04
    // SUMMARY). Matches the value stored in handName above.
    this.add.text(cx, titleY + 16, result.descr, {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#f5c542',
    }).setOrigin(0.5);

    // 5 cards centered.
    const totalW = 5 * CARD_GLYPH_VIEWPORT.cardW + 4 * CARD_GAP;
    const startX = cx - totalW / 2;
    for (let i = 0; i < result.cards.length; i++) {
      const card = result.cards[i];
      const x = startX + i * (CARD_GLYPH_VIEWPORT.cardW + CARD_GAP) + CARD_GLYPH_VIEWPORT.cardW / 2;
      makeCardGlyph(this, x, cardsY, card, CARD_GLYPH_VIEWPORT);
    }

    this.add.text(cx, scoreY, `Score: ${placeholderScore}`, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#f5e1c8',
    }).setOrigin(0.5);

    this.add.text(cx, scoreY + 24, 'press [M] menu  [ENTER/SPACE] share card', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#888888',
    }).setOrigin(0.5);

    // [M] still routes to MenuScene; [ENTER]/[SPACE] now route forward to
    // RunOverScene (D-FL-02 — Phase-2 share-card flow).
    this.input.keyboard?.on('keydown-M', () => this.scene.start('MenuScene'));
    this.input.keyboard?.on('keydown-ENTER', () => this.scene.start('RunOverScene'));
    this.input.keyboard?.on('keydown-SPACE', () => this.scene.start('RunOverScene'));
  }
}
